import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { getCountryFlag } from '../utils/countryFlags';

interface Season {
  id: string;
  name: string;
  gameType: string;
  countryCode: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  isPublic: boolean;
  order?: number;
  description: string;
  rules: {
    pointsForExactScore: number;
    pointsForCorrectResult: number;
    pointsForBanker: number;
    maxBankersPerRound: number;
  };
}

const Seasons: React.FC = () => {
  const { user } = useAuth();
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [participatedSeasonIds, setParticipatedSeasonIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSeasons = async () => {
      // Check if we have cached data with expiration
      const cachedSeasons = sessionStorage.getItem('seasons_list_data');
      const cacheTimestamp = sessionStorage.getItem('seasons_list_timestamp');
      
      if (cachedSeasons && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
          
          // Check if cache is still valid (less than 1 hour old)
          if (now - timestamp < oneHour) {
            const parsedSeasons = JSON.parse(cachedSeasons);
            setSeasons(parsedSeasons);
            setLoading(false);
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem('seasons_list_data');
            sessionStorage.removeItem('seasons_list_timestamp');
          }
        } catch (e) {
          // If parsing fails, remove invalid cache
          sessionStorage.removeItem('seasons_list_data');
          sessionStorage.removeItem('seasons_list_timestamp');
        }
      }
      
      // Only fetch if we don't have valid cached data
      try {
        setLoading(true);
        const response = await apiClient.get('/seasons');
        setSeasons(response.data);
        
        // Cache the data with timestamp
        sessionStorage.setItem('seasons_list_data', JSON.stringify(response.data));
        sessionStorage.setItem('seasons_list_timestamp', Date.now().toString());
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load seasons');
      } finally {
        setLoading(false);
      }
    };

    fetchSeasons();
  }, []);

  useEffect(() => {
    if (!user) {
      setParticipatedSeasonIds(new Set());
      return;
    }

    const fetchParticipatedSeasons = async () => {
      try {
        const response = await apiClient.get('/predictions/participated-seasons');
        setParticipatedSeasonIds(new Set(response.data));
      } catch {
        setParticipatedSeasonIds(new Set());
      }
    };

    fetchParticipatedSeasons();
  }, [user]);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-xl">Loading seasons...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-red-600 text-xl">{error}</div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Link to="/" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
            ← Back to Home
        </Link>
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Select Competition</h1>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Choose a competition to view upcoming matches and make predictions
          </p>
        </div>

        {/* Seasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {seasons.map(season => (
            <Link
              key={season.id}
              to={`/seasons/${season.id}`}
              className="card rounded-lg shadow hover:shadow-lg transition-shadow duration-200 overflow-hidden bg-gray-50 dark:bg-gray-800/50"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{getCountryFlag(season.countryCode)}</span>
                    <div>
                      <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{season.name}</h3>
{/*                       <p className="text-sm capitalize" style={{ color: 'var(--text-secondary)' }}>{season.gameType}</p>
 */}                    </div>
                  </div>
                  {participatedSeasonIds.has(season.id) && (
                    <span className="px-2 py-1 text-xs rounded-full font-medium whitespace-nowrap bg-highlight text-black">
                      Playing
                    </span>
                  )}
                </div>

                <p className="text-sm mb-4 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                  {season.description}
                </p>

{/*                 <div className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <div className="flex justify-between">
                    <span>Start Date:</span>
                    <span>{formatDate(season.startDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>End Date:</span>
                    <span>{formatDate(season.endDate)}</span>
                  </div>
                </div> */}
{/* 
                <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                  <div className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>Scoring Rules:</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                      <div className="font-semibold" style={{ color: 'var(--highlight)' }}>{season.rules.pointsForExactScore}</div>
                      <div style={{ color: 'var(--text-secondary)' }}>Exact Score</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold" style={{ color: 'var(--highlight)' }}>{season.rules.pointsForCorrectResult}</div>
                      <div style={{ color: 'var(--text-secondary)' }}>Correct Result</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold" style={{ color: 'var(--highlight)' }}>{season.rules.pointsForBanker}</div>
                      <div style={{ color: 'var(--text-secondary)' }}>Banker Bonus</div>
                    </div>
                  </div>
                </div> */}

                <div className="mt-4 flex justify-start">
                  <span className="text-sm font-medium mt-4" style={{ color: 'var(--highlight)' }}>
                    Make Predictions →
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {seasons.length === 0 && (
          <div className="text-center py-12">
            <p style={{ color: 'var(--text-secondary)' }}>No seasons available at the moment.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Seasons; 